import { AvatarSize, RawAvatar } from "@components/Avatar";
import { NonprofitAvatar } from "@components/Avatar/NonprofitAvatar";
import { LinkAppearance } from "@components/Link";
import {
  HeaderContainer,
  HeaderText,
  topBorderCss,
} from "@components/NewFundraiserCard/styles";
import { UserLink } from "@components/UserLink";
import React from "react";

import {
  FundraiserResponse,
  NonprofitResponse,
} from "@every.org/common/src/codecs/entities";
import { FundraiserType } from "@every.org/common/src/entity/types";
import { isOfficialFundraiser } from "@every.org/common/src/helpers/fundraisers";

import { useUser } from "src/context/UsersContext/hooks";
import { userOrUndefined } from "src/context/UsersContext/selectors";
import { truncatedTextCss } from "src/styles/truncatedTextCss";
import { colorCssVars } from "src/theme/color";
import { FontWeight, textSizeCss } from "src/theme/text";
export enum FundraiserCardHeaderSectionAppearance {
  DEFAULT = "DEFAULT",
  ALTERNATIVE = "ALTERNATIVE",
}

export const FundraiserCardHeaderSection = ({
  fundraiser,
  nonprofit,
  afterHeaderElement,
  showBorder,
  appearance = FundraiserCardHeaderSectionAppearance.DEFAULT,
}: {
  fundraiser: FundraiserResponse;
  nonprofit: NonprofitResponse;
  afterHeaderElement?: React.ReactNode;
  showBorder?: boolean;
  appearance?: FundraiserCardHeaderSectionAppearance;
}) => {
  const alternativeAppearance =
    appearance === FundraiserCardHeaderSectionAppearance.ALTERNATIVE;
  const user = userOrUndefined(
    useUser(
      fundraiser.creatorUserId ? { id: fundraiser.creatorUserId } : undefined
    )
  );

  const fromUser = !isOfficialFundraiser(fundraiser) ? user : undefined;

  const text = fromUser ? (
    <span>
      Fundraiser by{" "}
      <UserLink
        user={fromUser}
        appearance={LinkAppearance.HYPERLINK_UNCOLORED}
        contentCss={{ display: "inline" }}
        isTextLink
      />
    </span>
  ) : (
    <span>Official fundraiser</span>
  );

  if (!user && !isOfficialFundraiser(fundraiser)) {
    return null;
  }

  if (fundraiser.type === FundraiserType.MULTIPLE_NONPROFITS) {
    const name = fundraiser.metadata?.orgName;
    const avatarCloudinaryId = fundraiser.metadata?.avatarCloudinaryId;

    if (!name && afterHeaderElement && fundraiser.forNonprofitIds) {
      return (
        <HeaderContainer css={[showBorder && topBorderCss]}>
          <div css={truncatedTextCss({ numLines: 2 })}>
            <h5
              css={[
                textSizeCss.xs,
                {
                  color: `var(${colorCssVars.text.secondary})`,
                  fontWeight: FontWeight.REGULAR,
                },
              ]}
            >
              Fundraiser for {fundraiser.forNonprofitIds.length} nonprofits
              {fromUser && (
                <React.Fragment>
                  {" "}
                  by{" "}
                  <UserLink
                    user={fromUser}
                    appearance={LinkAppearance.HYPERLINK_UNCOLORED}
                    isTextLink
                  />
                </React.Fragment>
              )}
            </h5>
          </div>
          {afterHeaderElement}
        </HeaderContainer>
      );
    }

    if (!name) {
      return null;
    }

    return (
      <HeaderContainer css={showBorder && topBorderCss}>
        {avatarCloudinaryId && (
          <RawAvatar
            cloudinaryId={avatarCloudinaryId}
            size={
              alternativeAppearance ? AvatarSize.XX_SMALL : AvatarSize.SMALL
            }
          />
        )}
        <HeaderText>
          <h5>{name}</h5>
        </HeaderText>
      </HeaderContainer>
    );
  }

  return (
    <HeaderContainer css={showBorder && topBorderCss}>
      <NonprofitAvatar
        nonprofit={nonprofit}
        size={alternativeAppearance ? AvatarSize.XX_SMALL : AvatarSize.SMALL}
      />
      <HeaderText>
        {!alternativeAppearance && <h5>{nonprofit.name}</h5>}
        {text}
      </HeaderText>
      {afterHeaderElement}
    </HeaderContainer>
  );
};
